import {
	faArrowLeft,
	faRotateRight,
	faSpinner,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { formatDateSimple, formatPrice } from '@supply-mover-2-0/core';
import type {
	PortalAddressDto,
	PortalOrderDetailLineDto,
} from '@supply-mover-2-0/shared';
import {
	displayExternalId,
	getOrderStatusLabel,
	OrderStatus,
} from '@supply-mover-2-0/shared';
import {
	ImagePlaceholder,
	PromoChip,
	ResilientImage,
	Toast,
	useTranslation,
} from '@supply-mover-2-0/web-ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { JSX } from 'react';
import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AccountBreadcrumb } from '../components/AccountBreadcrumb';
import { CardChevron } from '../components/CardChevron';
import { ItemDesignationBadges } from '../components/ItemDesignationBadges';
import { DetailPageSkeleton } from '../components/skeletons';
import { useDisplaySettings } from '../hooks/useDisplaySettings';
import { usePortalAuth } from '../hooks/usePortalAuth';
import { formatCurrency } from '../lib/formatCurrency';
import { PAGE_CONTENT } from '../lib/pageWidth';
import * as cartService from '../services/portalCartService';
import * as orderService from '../services/portalOrderService';
import { useCartStore } from '../stores/cartStore';
import { useLoginModalStore } from '../stores/loginModalStore';

/** Map order status to a color scheme for the badge. */
function statusBadgeClasses(status: string): string {
	switch (status) {
		case OrderStatus.SHIPPED:
		case OrderStatus.PARTIALLY_SHIPPED:
		case OrderStatus.APPROVED:
		case OrderStatus.OVERRIDE_APPROVED:
			return 'bg-success-badge text-success-tint-fg border border-success-tint';
		case OrderStatus.CANCELLED:
		case OrderStatus.OVERRIDE_REJECTED:
			return 'bg-danger-badge text-danger-tint-fg border border-danger-tint';
		case OrderStatus.PENDING:
		case OrderStatus.SUBMITTED:
		case OrderStatus.SENT_TO_ERP:
			return 'bg-portal-accent/15 text-portal-accent';
		case OrderStatus.APPROVAL_REQUIRED:
		case OrderStatus.HELD:
		case OrderStatus.CANCELLATION_REQUESTED:
			return 'bg-warning-badge text-warning-tint-fg border border-warning-tint';
		default:
			return 'bg-brand-input text-strong';
	}
}

/** Render a billing or shipping address block. */
function AddressBlock({
	label,
	address,
	t,
}: {
	label: string;
	address: PortalAddressDto | null | undefined;
	t: (key: string) => string;
}): JSX.Element | null {
	if (address == null) {
		return null;
	}

	return (
		<div>
			<h3 className="text-xs font-semibold text-brand-muted uppercase mb-1">
				{t(label)}
			</h3>
			<p className="text-sm text-default">{t(address.name ?? '')}</p>
			<p className="text-sm text-brand-muted">{t(address.line1 ?? '')}</p>
			{address.line2 != null && (
				<p className="text-sm text-brand-muted">{t(address.line2)}</p>
			)}
			<p className="text-sm text-brand-muted">
				{t(address.city ?? '')}
				{address.city != null && address.state != null ? ', ' : ''}
				{t(address.state ?? '')} {t(address.zip ?? '')}
			</p>
			{address.country != null && (
				<p className="text-sm text-brand-muted">{t(address.country)}</p>
			)}
		</div>
	);
}

/**
 * Order detail page -- shows order header, summary, addresses, line items,
 * and a reorder button. Data fetched via TanStack Query.
 */
export function OrderDetailPage(): JSX.Element {
	const { t } = useTranslation();
	const { id } = useParams<{ id: string }>();
	const navigate = useNavigate();
	const { token, isAuthenticated } = usePortalAuth();
	const display = useDisplaySettings();
	const openLoginModal = useLoginModalStore((s) => s.open);
	const queryClient = useQueryClient();

	const orderId = id != null ? Number(id) : null;
	const queryKey = ['order', orderId];

	const { data, isLoading } = useQuery({
		queryKey,
		queryFn: () => orderService.getOrder(token as string, orderId as number),
		enabled: token != null && orderId != null,
	});

	const setCart = useCartStore((s) => s.setCart);
	const reorderMutation = useMutation({
		mutationFn: () => orderService.reorder(token as string, orderId as number),
		onSuccess: async (result) => {
			// PD-16171 — lines the storefront could not add (no price, no longer
			// sold online) are skipped, not silently dropped: name them.
			if (result.skippedItems.length > 0) {
				Toast.show({
					type: 'warning',
					text1: t('Some items were not added to your cart'),
					text2: result.skippedItems
						.map((skipped) => t(skipped.message))
						.join('\n'),
					duration: 10000,
				});
			}
			// Refresh cart store before navigating so cart page shows updated items
			if (token != null) {
				try {
					const cartRes = await cartService.getCart(token);
					setCart(cartRes.data);
				} catch {}
			}
			navigate('/cart');
		},
		// PD-16171 — when none of the order's items can be added, the server
		// refuses the reorder and names them; show that instead of nothing.
		onError: (error) => {
			Toast.show({
				type: 'error',
				text1: t('Could not reorder'),
				text2: error instanceof Error ? t(error.message) : undefined,
			});
		},
	});

	// Signed-out visitors get the login modal over an empty page rather than
	// a perpetually-empty order detail, matching Invoice/Statement detail
	// (PD-14185).
	useEffect(() => {
		if (!isAuthenticated) {
			openLoginModal();
		}
	}, [isAuthenticated, openLoginModal]);

	const order = data?.data;

	if (!isAuthenticated) {
		return <div />;
	}

	if (isLoading) {
		// Skeleton shaped like the detail layout (PD-14692); the reorder
		// button spinner below is the only spinner left on the page.
		return (
			<div className={`${PAGE_CONTENT} px-4 py-4`}>
				<DetailPageSkeleton />
			</div>
		);
	}

	if (order == null) {
		return (
			<div className="px-4 py-12 text-center">
				<p className="text-sm text-brand-muted">{t('Order not found.')}</p>
				<button
					type="button"
					onClick={() => navigate('/orders')}
					className="mt-4 px-4 py-2 text-sm text-portal-accent hover:text-portal-accent dark:text-portal-accent dark:hover:text-portal-accent"
				>
					{t('Back to Orders')}
				</button>
			</div>
		);
	}

	const lineItems: Array<PortalOrderDetailLineDto> = order.lineItems ?? [];

	return (
		<div className={`px-4 py-4 ${PAGE_CONTENT}`}>
			{/* PD-17267 — desktop gets the account breadcrumb the list pages
			    have, with Account and Orders both clickable. */}
			<AccountBreadcrumb
				parent={{ label: 'Orders', path: '/orders' }}
				current={`${t('Order')} #${displayExternalId(order) ?? ''}`}
			/>
			{/* Mobile back button — explicit list navigation, not navigate(-1):
			    after visiting a line item the previous history entry is the
			    line receipt, so popping would land back on it (same fix as
			    InvoiceDetailPage). */}
			<button
				type="button"
				onClick={() => navigate('/orders')}
				className="md:hidden flex items-center gap-2 text-sm text-brand-muted hover:text-default mb-4 min-h-touch"
			>
				<FontAwesomeIcon icon={faArrowLeft} />
				<span>{t('Back to Orders')}</span>
			</button>

			{/* Order header */}
			<div className="flex items-start justify-between mb-6">
				<div>
					<h1 className="text-lg md:text-xl font-bold text-default">
						{t('Order')} {t('#')}
						{t(displayExternalId(order) ?? '')}
					</h1>
					<p className="text-sm text-brand-muted">
						{t('Placed on')}:{' '}
						{/* PD-16742: order.date is a full timestamp, so formatDateSimple
						    reads it in the shopper's own day and prints M/D/YY. */}
						{t(formatDateSimple(order.date))}
					</p>
				</div>
				<span
					className={`px-2.5 py-1 text-xs font-medium rounded-full ${statusBadgeClasses(order.status ?? '')}`}
				>
					{t(getOrderStatusLabel(order.status))}
				</span>
			</div>

			{/* Order summary card */}
			<div className="bg-brand-surface border border-brand-border rounded-xl p-4 mb-4">
				<h2 className="text-sm font-semibold text-default mb-3">
					{t('Order Summary')}
				</h2>
				<div className="space-y-2 text-sm">
					{order.customerPO != null && (
						<div className="flex justify-between text-brand-muted">
							<span>{t('PO Number')}</span>
							<span>{t(order.customerPO)}</span>
						</div>
					)}
					{/* PD-14771: the Ship Via row is hidden for tenants whose
					    integration writes an internal code into shipVia that
					    isn't meaningful to a customer (e.g. HF's own-delivery
					    route codes). `showShipVia` is backfilled to true for
					    older APIs that omit it (see useDisplaySettings). */}
					{display.showShipVia &&
						(order.shipViaLabel ?? order.shipVia) != null && (
							<div className="flex justify-between text-brand-muted">
								<span>{t('Ship Via')}</span>
								<span>{t(order.shipViaLabel ?? order.shipVia ?? '')}</span>
							</div>
						)}
					{/* PD-14239: order-summary money rows (subtotal, promos,
					    discount, tax, shipping, total) are hidden when this portal
					    user cannot see pricing. Non-money rows (PO, Ship Via) stay.
					    PD-16743: every amount goes through formatCurrency, as the
					    line items do, so $2,282.76 keeps its thousands separator. */}
					{display.canSeePricing && (
						<div className="flex justify-between text-brand-muted">
							<span>{t('Subtotal')}</span>
							<span>
								{t(formatCurrency(order.subtotal) ?? '$0.00')}
							</span>
						</div>
					)}
					{/* Promo savings — mirrors the cart's order-summary
					    treatment so a customer reviewing a placed order sees
					    the same Savings line they confirmed at checkout.
					    Single promo collapses into one named row; multi-promo
					    orders get an aggregate Savings parent + per-promo
					    breakdown. */}
					{display.canSeePricing &&
						order.appliedPromos != null &&
						order.appliedPromos.length > 0 &&
						(() => {
							const promos = order.appliedPromos;
							const total = promos.reduce(
								(sum, p) => sum + Number(p.discountAmount),
								0,
							);
							if (promos.length === 1) {
								const p = promos[0];
								return (
									<div className="flex justify-between text-success">
										<span className="font-medium truncate">
											{t(p.promoName)}
											{p.couponCodeUsed != null && (
												<span className="ml-1 text-brand-muted text-xs">
													({t(p.couponCodeUsed)})
												</span>
											)}
										</span>
										<span>
											-{t(formatCurrency(p.discountAmount) ?? '$0.00')}
										</span>
									</div>
								);
							}
							return (
								<>
									<div className="flex justify-between text-success">
										<span className="font-medium">{t('Savings')}</span>
										<span>
											-{t(formatCurrency(total) ?? '$0.00')}
										</span>
									</div>
									{promos.map((p) => (
										<div
											key={p.promoId}
											className="flex justify-between text-xs text-success pl-2"
										>
											<span className="truncate">
												{t(p.promoName)}
												{p.couponCodeUsed != null && (
													<span className="ml-1 text-brand-muted">
														({t(p.couponCodeUsed)})
													</span>
												)}
											</span>
											<span>
												-{t(formatCurrency(p.discountAmount) ?? '$0.00')}
											</span>
										</div>
									))}
								</>
							);
						})()}
					{display.canSeePricing &&
						order.discount != null &&
						Number(order.discount) > 0 && (
							<div className="flex justify-between text-brand-muted">
								<span>{t('Discount')}</span>
								<span>
									-{t(formatCurrency(order.discount) ?? '$0.00')}
								</span>
							</div>
						)}
					{display.canSeePricing && order.tax != null && (
						<div className="flex justify-between text-brand-muted">
							<span>{t('Tax')}</span>
							<span>
								{t(formatCurrency(order.tax) ?? '$0.00')}
							</span>
						</div>
					)}
					{display.canSeePricing && order.freight != null && (
						<div className="flex justify-between text-brand-muted">
							<span>{t('Shipping')}</span>
							<span>
								{t(formatCurrency(order.freight) ?? '$0.00')}
							</span>
						</div>
					)}
					{display.canSeePricing && (
						<div className="flex justify-between font-semibold text-default pt-2 border-t border-subtle">
							<span>{t('Total')}</span>
							<span>
								{t(formatCurrency(order.total) ?? '$0.00')}
							</span>
						</div>
					)}
				</div>
			</div>

			{/* Addresses */}
			{(order.billingAddress != null || order.shippingAddress != null) && (
				<div className="bg-brand-surface border border-brand-border rounded-xl p-4 mb-4">
					<div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
						<AddressBlock
							label="Billing Address"
							address={order.billingAddress}
							t={t}
						/>
						<AddressBlock
							label="Shipping Address"
							address={order.shippingAddress}
							t={t}
						/>
					</div>
				</div>
			)}

			{/* Line items */}
			<div className="bg-brand-surface border border-brand-border rounded-xl p-4 mb-4">
				<h2 className="text-sm font-semibold text-default mb-3">
					{t('Line Items')} ({t(String(lineItems.length))})
				</h2>

				{lineItems.length === 0 ? (
					<p className="text-sm text-brand-muted text-center py-4">
						{t('No line items.')}
					</p>
				) : (
					<div className="space-y-3">
						{lineItems.map((line: PortalOrderDetailLineDto) => {
							// Per-line promo lookup: if this line was discounted
							// by one or more promos, we render a strikethrough
							// + final price + chip(s) instead of the plain
							// subtotal. Mirrors the cart's row treatment so the
							// signed-receipt view tells the same story.
							const linePromo = (order.linePromos ?? []).find(
								(lp) => lp.orderDetailId === line.id,
							);
							return (
								<button
									key={line.id}
									type="button"
									// Opens the receipt-style line detail (what was bought, at
									// the price paid) — not the live catalog product page. Works
									// in every storefront mode and for non-item lines (keyed on
									// line.id, not fkItemId).
									onClick={() =>
										navigate(`/orders/${id ?? ''}/line/${line.id}`)
									}
									className="flex gap-3 border-b border-subtle pb-3 last:border-0 last:pb-0 text-left w-full hover:bg-brand-input -mx-2 px-2 rounded-lg transition-colors"
								>
									{/* Item image. PD-17144: removed with its box when the brand
									    hides item images; the details take the row. */}
									{display.showItemImages && (
										<div className="w-14 h-14 shrink-0 bg-brand-input rounded-lg flex items-center justify-center overflow-hidden">
											<ResilientImage
												src={line.primaryImageUrl}
												alt={t(line.description ?? '')}
												className="w-full h-full object-cover"
												onLikelyStaleUrl={() => {
													void queryClient.invalidateQueries({ queryKey });
												}}
												fallback={<ImagePlaceholder size="sm" />}
											/>
										</div>
									)}

									{/* Item details */}
									<div className="flex-1 min-w-0">
										<h3 className="text-sm font-medium text-default truncate">
											{t(line.description ?? '')}
										</h3>
										{line.itemExternalId != null && (
											<p className="text-xs text-brand-muted">
												{t(line.itemExternalId)}
											</p>
										)}
										{line.designations != null && (
											<div className="mt-0.5">
												<ItemDesignationBadges
													designations={line.designations}
												/>
											</div>
										)}
										{linePromo != null &&
											linePromo.appliedPromos.length > 0 && (
												<div className="mt-1 flex flex-wrap gap-1">
													{linePromo.appliedPromos.map((p) => (
														<PromoChip
															key={p.promoId}
															variant="inline"
															name={t(p.promoName)}
														/>
													))}
												</div>
											)}
										<div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1 text-xs text-brand-muted">
											<span>
												{t('Ordered')}: {t(String(line.quantityOrdered ?? 0))}
											</span>
											<span>
												{t('Shipped')}: {t(String(line.quantityShipped ?? 0))}
											</span>
											{line.quantityBackordered != null &&
												Number(line.quantityBackordered) > 0 && (
													<span className="text-warning">
														{t('Backordered')}:{' '}
														{t(String(line.quantityBackordered))}
													</span>
												)}
										</div>
									</div>

									{/* Price. Subtotal stays at 2 decimals (currency math —
								    invoice arithmetic is always 2dp). Unit price runs
								    through `formatPrice`, which honors the tenant's
								    `maxPriceDecimalPrecision` setting seeded into
								    ItemSettingsService at bootstrap. Default tenants
								    see "$69.00"; pharmacy / per-unit foodservice
								    tenants who configure 4+ decimals get the extra
								    precision they need. Either way the DB column's
								    `decimal(15,6)` doesn't leak ("$69.000000"). */}
									{/* PD-14239: hide the entire per-line price column
									    (subtotal, promo strikethrough/discounted, and unit
									    price) when this portal user cannot see pricing. */}
									{display.canSeePricing && (
										<div className="text-right shrink-0">
											{linePromo != null &&
											Number(linePromo.lineDiscountTotal) > 0 ? (
												<>
													<p className="text-xs text-brand-muted line-through">
														{t(
															formatCurrency(linePromo.originalSubtotal) ??
																'$0.00',
														)}
													</p>
													<p className="text-sm font-semibold text-default">
														{t(
															formatCurrency(linePromo.finalSubtotal) ??
																'$0.00',
														)}
													</p>
												</>
											) : (
												<p className="text-sm font-semibold text-default">
													{t(formatCurrency(line.subtotal) ?? '$0.00')}
												</p>
											)}
											<p className="text-xs text-brand-muted">
												{t('@')} {t(formatPrice(line.price))}
											</p>
										</div>
									)}
									<CardChevron />
								</button>
							);
						})}
					</div>
				)}
			</div>

			{/* Reorder button — purchasing action, so hidden on browse-only and
			    account-portal-only storefronts (canPurchase off). The order
			    itself still renders so customers can review history. */}
			{display.canPurchase && (
				<button
					type="button"
					onClick={() => {
						reorderMutation.mutate();
					}}
					disabled={reorderMutation.isPending}
					className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-portal-accent hover:bg-portal-accent-hover disabled:bg-portal-accent/60 text-white font-medium rounded-lg transition-colors min-h-touch"
				>
					{reorderMutation.isPending ? (
						<FontAwesomeIcon icon={faSpinner} className="animate-spin" />
					) : (
						<FontAwesomeIcon icon={faRotateRight} />
					)}
					<span>
						{reorderMutation.isPending ? t('Reordering...') : t('Reorder')}
					</span>
				</button>
			)}
		</div>
	);
}
